import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { getStaffToken, setStaffToken, staffApi, staffLogin, staffLogout, type StaffUser } from '../api/staff';
import { apiFetch } from '../api/client';

interface StaffAuthContextValue {
  user: StaffUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const StaffAuthContext = createContext<StaffAuthContextValue | null>(null);

export function StaffAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<StaffUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    const token = getStaffToken();
    if (!token) {
      setUser(null);
      return;
    }
    try {
      const me = await apiFetch<StaffUser>('/api/account/me', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(me);
    } catch {
      setStaffToken(null);
      setUser(null);
    }
  }, []);

  useEffect(() => {
    refreshUser().finally(() => setLoading(false));
  }, [refreshUser]);

  const login = useCallback(async (email: string, password: string) => {
    const result = await staffLogin(email, password);
    setUser(result.user);
  }, []);

  const logout = useCallback(async () => {
    await staffLogout();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      isAuthenticated: !!user,
      login,
      logout,
      refreshUser,
    }),
    [user, loading, login, logout, refreshUser]
  );

  return <StaffAuthContext.Provider value={value}>{children}</StaffAuthContext.Provider>;
}

export function useStaffAuth(): StaffAuthContextValue {
  const ctx = useContext(StaffAuthContext);
  if (!ctx) {
    throw new Error('useStaffAuth debe usarse dentro de StaffAuthProvider');
  }
  return ctx;
}

export { staffApi };
